import React, { useEffect, useState } from "react";
import { getDashboard } from "../lib/api";
import { eur, PRIORITY } from "../lib/constants";
import { ScoreBadge } from "../components/Badges";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid,
} from "recharts";
import {
  Users, Flame, CalendarCheck, FileText, TrendingUp, Trophy, CircleDollarSign, Clock,
} from "lucide-react";

const FUNNEL_MARGIN = { left: 20, right: 30 };
const X_TICK = { fill: "#64748B", fontSize: 11 };
const Y_TICK = { fill: "#94A3B8", fontSize: 12 };
const TOOLTIP_CURSOR = { fill: "rgba(255,255,255,0.04)" };
const TOOLTIP_STYLE = { background: "#131823", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 };
const BAR_RADIUS = [0, 6, 6, 0];

export default function Dashboard({ onOpenProspect }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    getDashboard().then(setData).catch((e) => console.warn("dashboard load failed", e));
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Caricamento...</div>;

  const kpis = [
    { label: "Prospect Attivi", value: data.active, icon: Users, color: "#0EA5E9" },
    { label: "Hot Leads", value: data.hot, icon: Flame, color: "#EF4444" },
    { label: "Appuntamenti", value: data.meetings, icon: CalendarCheck, color: "#F97316" },
    { label: "Proposte", value: data.proposals, icon: FileText, color: "#A855F7" },
    { label: "Clienti Acquisiti", value: data.won, icon: Trophy, color: "#22C55E" },
    { label: "Conversion Rate", value: `${data.conversion_rate}%`, icon: TrendingUp, color: "#06B6D4" },
    { label: "Pipeline Value", value: eur(data.pipeline_value), icon: CircleDollarSign, color: "#EAB308" },
    { label: "Valore Acquisito", value: eur(data.won_value), icon: CircleDollarSign, color: "#22C55E" },
  ];

  return (
    <div className="fade-in space-y-8 p-6 md:p-8">
      <Header />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="card-lift rounded-xl border border-border bg-card p-5" data-testid={`kpi-${k.label}`}>
            <div className="flex items-center justify-between">
              <span className="label-caps text-muted-foreground">{k.label}</span>
              <k.icon className="h-4 w-4" style={{ color: k.color }} strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-heading text-3xl font-bold tracking-tight" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-1 font-heading text-lg font-semibold">Funnel di Conversione</h3>
          <p className="mb-5 text-xs text-muted-foreground">Dalla ricerca al cliente acquisito</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} layout="vertical" margin={FUNNEL_MARGIN}>
                <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={X_TICK} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="stage" tick={Y_TICK} width={90} axisLine={false} tickLine={false} />
                <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="value" radius={BAR_RADIUS} barSize={24} isAnimationActive={false} fill="#0EA5E9">
                  {data.funnel.map((f, i) => <Cell key={f.stage} fill={`hsl(199, 89%, ${58 - i * 5}%)`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-heading text-lg font-semibold">Priorità</h3>
          <div className="space-y-3">
            {Object.entries(PRIORITY).map(([key, cfg]) => {
              const v = data.by_priority[key] || 0;
              const pct = data.total ? (v / data.total) * 100 : 0;
              return (
                <div key={key}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span style={{ color: cfg.color }}>{cfg.label}</span>
                    <span className="tabular-nums text-muted-foreground">{v}</span>
                  </div>
                  <div className="h-2 w-full rounded bg-secondary">
                    <div className="h-full rounded" style={{ width: `${pct}%`, background: cfg.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ListCard title="Azioni di oggi" icon={Clock} empty="Nessuna azione in scadenza"
          items={data.todo} onOpenProspect={onOpenProspect} showAction />
        <ListCard title="🔥 Hot Leads" icon={Flame} empty="Nessun hot lead — analizza i tuoi prospect"
          items={data.hot_leads} onOpenProspect={onOpenProspect} />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="label-caps text-primary">20/80LAB · Client Acquisition System 1.0</div>
      <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">Sales Dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">20% del tempo per trovare · 80% per convertire.</p>
    </div>
  );
}

function ListCard({ title, icon: Icon, items, empty, onOpenProspect, showAction }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold">
        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} /> {title}
      </h3>
      {(!items || items.length === 0) && <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>}
      <div className="space-y-2">
        {items?.map((p) => (
          <button key={p.id} onClick={() => onOpenProspect(p)} data-testid={`dash-lead-${p.id}`}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-secondary/40">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{p.azienda}</div>
              <div className="truncate text-xs text-muted-foreground">
                {showAction ? (p.next_action || "Nessuna azione") : (p.servizio_consigliato || p.settore || "—")}
              </div>
            </div>
            <ScoreBadge score={p.score} priority={p.priority} size="sm" />
          </button>
        ))}
      </div>
    </div>
  );
}
